import { ColumnsWidth, ColumnType, CustomCellProps, GridData, MassActionsProps } from 'components/common/grid';
import MITable from 'components/common/grid/MITable';
import React from 'react';
import useBundleTranslation from 'i18n';
import AggregationSelect from './AggregationSelect';
import AggregationLabel from './AggregationLabel';
import DerivedControl from './DerivedControl';
import { Box } from '@mui/material';
import { RulesConfig } from 'components/dataset-viewer/index';

function NumericFieldsSelectorTable({
    data,
    selectedColumns,
    onSelectedChange,
    onAggregationChange,
    onDerivedAction,
    filter,
    maxHeight,
}: {
    data: GridData;
    selectedColumns: string[];
    onSelectedChange: (keys: string[], checked: boolean) => void;
    onAggregationChange: (fieldId: string, value: string, oldValue: string) => void;
    onDerivedAction?: (action: 'edit' | 'delete', fieldId: string) => void;
    filter: RulesConfig;
    maxHeight?: number;
}) {
    const { t } = useBundleTranslation(['components/dataset-viewer/dataset_viewer']);

    const columns: ColumnType[] = [
        {
            title: t('fields_selector.number_fields_label'),
            name: 'aggregationLabel',
            resizable: false,
        },
        {
            name: 'aggregationSelector',
            title: t('fields_selector.aggregation_column'),
            resizable: false,
        },
        {
            name: 'derivedControl',
            resizable: false,
            title: '',
        },
    ];

    const columnWidth: ColumnsWidth = {
        derivedControl: 70,
    };

    const handleCheckRow = (event: any) => {
        const checked = event.target.checked;
        const value = event.target.value;
        onSelectedChange([value], checked);
    };

    const handleCheckAllRows = (event: any) => {
        const checked = event.target.checked;
        onSelectedChange(
            data.map((item) => item.id as string),
            checked,
        );
    };

    const massActionsProps: MassActionsProps = {
        checkedRowsKeys: selectedColumns,
        handleCheckRow: handleCheckRow,
        handleCheckAllRows: handleCheckAllRows,
        checkAllChecked: () => {
            let result = true;

            data.forEach((item) => {
                if (result && !selectedColumns.includes(item.id as string)) {
                    result = false;
                    return;
                }
            });

            return result;
        },
    };

    const checkDerivedDeps = (id: string) => {
        let result = true;

        const derivedFieldConfig = filter.derivedFields.find((d) => d.id === id);

        if (derivedFieldConfig) {
            const relatedFields = derivedFieldConfig.info?.relatedColumns;

            relatedFields?.forEach((field) => {
                const filedId = field.field;
                if (
                    filter.resultFieldAggregations.hasOwnProperty(filedId) &&
                    filter.resultFieldAggregations[filedId] > ''
                ) {
                    result = false;
                    return;
                }
            });
        }

        return result;
    };

    return data.length > 0 ? (
        <Box
            sx={{
                '.rs-table-row--selected-row .rs-table-cell': { backgroundColor: 'background.default' },
            }}
        >
            <MITable
                height={maxHeight}
                gridName={'dataset_view_fields'}
                columnsWidth={columnWidth}
                data={data}
                columns={columns}
                massActionsConfig={{ enabled: true }}
                massActionsProps={massActionsProps}
                customCells={{
                    aggregation: ({ data }: CustomCellProps) => {
                        const selected = data && selectedColumns.includes(data.id as string);
                        const derived = data ? data.derived : false;
                        let showAggregation = true;

                        if (derived && data) {
                            showAggregation = checkDerivedDeps(data.id as string);
                        }

                        if (selected && showAggregation) {
                            return <AggregationSelect row={data ?? {}} onChange={onAggregationChange} />;
                        }

                        return null;
                    },
                    aggregationLabel: ({ data }: CustomCellProps) => {
                        const selected = Boolean(data && selectedColumns.includes(data.id as string));

                        return <AggregationLabel row={data ?? {}} selected={selected} />;
                    },
                    derivedControl: ({ data }: CustomCellProps) => {
                        const derived = data ? data.derived : false;

                        if (derived && onDerivedAction) {
                            return <DerivedControl row={data ?? {}} onDerivedAction={onDerivedAction} />;
                        }

                        return null;
                    },
                }}
            />
        </Box>
    ) : null;
}

export default NumericFieldsSelectorTable;
